<script lang="ts">
	/**
	 * Edits a list of free-form strings (tags, allowed domains, list options) as
	 * a text field plus a row of removable chips.
	 *
	 * The input is wrapped in a `<form>` purely so pressing Enter commits an
	 * entry: it is the gesture users already expect from tag inputs, and it keeps
	 * the component from needing an "Add" button that would compete with the
	 * surrounding form's real submit button.
	 */

	import { Badge, Button, Input } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import { toastWarning } from '../stores/toast';

	let cssClass: string = '';
	export { cssClass as class };

	/** Two-way bindable list of values. */
	export let values: string[] = [];

	/** Placeholder for the "new entry" field. */
	export let placeHolder: string = 'New Value';
	export let disabled: boolean = false;
	export let size: 'sm' | 'md' | 'lg' | undefined = 'md';

	/** Chip colour — pick one that reads as "data", not as a status. */
	export let color: 'red' | 'yellow' | 'green' | 'indigo' | 'purple' | 'pink' | 'blue' | 'dark' | 'primary' | 'none' = 'yellow';

	/** Forwarded to the text input so an external `<label for=...>` can target it. */
	export let id: string = '';

	// `onChange` fires on every add/remove so a parent can mark its form dirty
	// without having to diff the bound array.
	const dispatch = createEventDispatcher<{ onChange: { values: string[] } }>();

	let newEntryName: string = '';

	const fnCreateNewEntry = (event: SubmitEvent): void =>
	{
		event.preventDefault();
		newEntryName = newEntryName.trim();
		if (newEntryName.length == 0) return;

		values ??= [];

		// Case-insensitive: "Sales" and "sales" are the same tag to a human, and
		// silently accepting both produces lists nobody can clean up later.
		if (values.find((existingValue) => existingValue.toLowerCase() == newEntryName.toLowerCase()) != null)
		{
			toastWarning('The entered value already exists.');
			return;
		}

		values.push(newEntryName);
		newEntryName = '';

		values = values;

		dispatch('onChange', { values });
	};

	const fnRemoveEntry = (value: string): void =>
	{
		// Deferred a beat so the chip's own click/hover transition finishes before
		// the node is torn out from under the cursor.
		setTimeout(() =>
		{
			values = values.filter((existingValue) => existingValue != value);
			dispatch('onChange', { values });
		}, 100);
	};
</script>

<div class={cssClass}>
	<form on:submit={fnCreateNewEntry}>
		<Input type="text" {id} {size} bind:value={newEntryName} placeholder={placeHolder} {disabled} />
	</form>
	{#if values?.length > 0}
		<div class="flex flex-wrap gap-2 mt-2">
			{#each values as value, index (index)}
				<Badge {color}>
					{value}
					{#if !disabled}
						<Button class="!p-0 px-0 ml-1 hover:text-white" color="none" on:click={() => fnRemoveEntry(value)}>
							<i class="fa-solid fa-xmark" />
						</Button>
					{/if}
				</Badge>
			{/each}
		</div>
	{/if}
</div>
